import {
  ActionReducerMapBuilder,
  createAction,
  EntityState,
  EntityId,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';

import { ThunkExtra } from '@app/store';
import { UserRoleEnum } from '@app/types';
import { NavigationExternalItem } from '@app/router';
import { GetSideMenuResponse } from '@app/gen/side_menu';
import {
  ContentSearchRequest,
  ContentSearchResponse,
  SearchService,
} from '@app/gen/search';

import { LayoutState } from '../layout.state';
import { LayoutService } from '../../services';

export const setServiceUnavailable = createAction<boolean>(
  'layout/setServiceUnavailable',
);

export const setSideMenu =
  createAction<GetSideMenuResponse>('layout/setSideMenu');

export const setSidebarState = createAction<boolean>('layout/setSidebarState');

export const setIsLogoLoaded = createAction<boolean>('layout/setIsLogoLoaded');

export const loadCandidateLogo = createAsyncThunk<
  void,
  { id: string | null },
  ThunkExtra
>(
  'layout/loadCandidateLogo',
  async (dto, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const layoutService = inject(LayoutService);

      const { id } = dto;

      if (id === null) {
        dispatch(setIsLogoLoaded(false));

        return;
      }

      const res = await layoutService.getCandidateLogo(id);

      if (res) {
        dispatch(setIsLogoLoaded(true));
      }
    } catch (err) {
      dispatch(setIsLogoLoaded(false));

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const search = createAsyncThunk<
  ContentSearchResponse,
  ContentSearchRequest,
  ThunkExtra
>('layout/search', async (params, { rejectWithValue, extra: { inject } }) => {
  try {
    const searchService = inject(SearchService);
    const result = await searchService.contentSearch({
      body: params,
    });

    return result;
  } catch (err) {
    return rejectWithValue(classToPlain(err) as AppError);
  }
});

export const getSideMenu = createAsyncThunk<
  GetSideMenuResponse,
  undefined,
  ThunkExtra
>(
  'layout/getSideMenu',
  async (dto, { rejectWithValue, dispatch, extra: { inject } }) => {
    try {
      const layoutService = inject(LayoutService);
      const result = await layoutService.getSideMenu();
      dispatch(setSideMenu(result));
      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const resetState = createAction('layout/resetState');

export const getAdditionalMenuItems = createAsyncThunk<
  Record<UserRoleEnum, NavigationExternalItem[]>,
  undefined,
  ThunkExtra
>(
  'layout/getAdditionalMenuItems',
  async (dto, { rejectWithValue, extra: { inject } }) => {
    try {
      const layoutService = inject(LayoutService);

      const result = await layoutService.getAdditionalMenuItems();

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const layoutReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<
      {
        id: EntityId;
      },
      EntityId
    > &
      LayoutState
  >,
) => {
  builder.addCase(setServiceUnavailable, (state, action) => {
    state.isServiceUnavailable = action.payload;
  });

  builder.addCase(setSidebarState, (state, action) => {
    state.sidebarState = action.payload;
  });
  builder.addCase(setSideMenu, (state, action) => {
    state.sideMenuState = action.payload;
  });

  builder.addCase(setIsLogoLoaded, (state, action) => {
    state.isLogoLoaded = action.payload;
  });

  builder.addCase(loadCandidateLogo.pending, (state) => {
    state.logoLoadingStatus = 'loading';
  });
  builder.addCase(loadCandidateLogo.fulfilled, (state) => {
    state.logoLoadingStatus = 'complete';
  });
  builder.addCase(loadCandidateLogo.rejected, (state, { payload, error }) => {
    state.logoLoadingStatus = 'failed';
    state.logoLoadingError =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(search.pending, (state) => {
    state.search.status = 'loading';
  });
  builder.addCase(search.fulfilled, (state, { payload, meta }) => {
    state.search.status = 'complete';
    state.search.group = meta.arg?.group;
    state.search.items = payload;
  });
  builder.addCase(search.rejected, (state, { payload, error }) => {
    state.search.status = 'failed';
    state.search.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });

  builder.addCase(getSideMenu.pending, (state) => {
    state.sideMenuStatus = 'loading';
    state.sideMenuState = null;
  });
  builder.addCase(getSideMenu.fulfilled, (state, { payload }) => {
    state.sideMenuStatus = 'complete';
    state.sideMenuState = payload;
  });
  builder.addCase(getSideMenu.rejected, (state, { payload, error }) => {
    state.sideMenuStatus = 'failed';
    state.sideMenuState = null;
    state.sideMenuError =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
};
